import { useEffect, useMemo } from 'react';
import { parseISO } from 'date-fns';
import { captureError } from '@/lib/logger';
import { ContractInsightsData } from '@/pages/ContractDetail';

/**
 * Overdue interest, as the database computed it.
 *
 * The engine is `public.calculate_overdue_interest`, composed into `get_contract_insights`
 * (migration 20260823010000). This hook no longer computes anything: it reads the `overdue`
 * payload the contract page already fetched and turns the server's DATE strings into Dates on
 * the viewer's own calendar — `parseISO('2026-01-05')` is local midnight, which is what the
 * week Mondays the components compare against are too.
 */

/** One priced sale (a repayment, or the synthetic Monday accrual row the engine emits as id -1). */
export interface OverdueSale {
    id: number;
    amount: number;
    paid_at: Date;
    description: string | null;
    remarks: string | null;
    overdueInterest: number | null;
    applyOverdueInterest: boolean;
    surplusOverdueInterest: number | null;
}

/** One Monday-start week: what was expected, what arrived, and what it cost. */
export interface OverdueWeek {
    monday: Date;
    expected: number;
    totalPaid: number;
    totalInterest: number;
    sales: OverdueSale[];
}

interface UseOverdueInterestResult {
    /** Signed: negative is interest owed. Call sites take the absolute value for display. */
    totalOverdueInterest: number;
    weeks: OverdueWeek[];
    /** Every priced sale, in week order — the Interest tab's surplus total reduces over this. */
    sales: OverdueSale[];
    /** Monday of the first fully-paid week; interest starts the Monday after it. */
    firstFullPaymentWeek: Date | null;
}

export function useOverdueInterest(data: ContractInsightsData | undefined): UseOverdueInterestResult {
    const overdue = data?.overdue ?? null;
    const contractId = data?.contract?.id;

    // The rate tripwire, kept where the old client calculator carried it.
    // `contracts.overdue_interest_rate` is NOT NULL DEFAULT 365, so `rate_missing` is
    // structurally unreachable — if it ever arrives true, that is a defect worth a report,
    // not a contract that is legitimately unpriced.
    const rateMissing = overdue?.rate_missing === true;
    useEffect(() => {
        if (!rateMissing) return;
        captureError(
            new Error('Overdue interest not computed: contract has no overdue_interest_rate'),
            { tags: { area: 'overdue-interest' }, extra: { contractId } }
        );
    }, [rateMissing, contractId]);

    return useMemo(() => {
        const weeks: OverdueWeek[] = (overdue?.weeks ?? []).map(week => ({
            monday: parseISO(week.monday),
            expected: week.expected,
            totalPaid: week.total_paid,
            totalInterest: week.total_interest,
            sales: (week.sales ?? []).map(sale => ({
                id: sale.id,
                amount: sale.amount,
                paid_at: parseISO(sale.paid_at),
                description: sale.description,
                remarks: sale.remarks,
                overdueInterest: sale.overdue_interest,
                applyOverdueInterest: sale.apply_overdue_interest,
                surplusOverdueInterest: sale.surplus_overdue_interest,
            })),
        }));

        return {
            totalOverdueInterest: overdue?.total_overdue_interest ?? 0,
            weeks,
            sales: weeks.flatMap(week => week.sales),
            firstFullPaymentWeek: overdue?.first_full_payment_week
                ? parseISO(overdue.first_full_payment_week)
                : null,
        };
    }, [overdue]);
}
